import { PaginationLoader, type PaginationLoaderDelegate } from '@/components/pagination/PaginationLoader';
import { ITEMS_PER_PAGE } from '@/constants';
import { default as apiService } from '@/services/api.ts';
import { useAuthStore } from '@/stores/auth';
import { useSortingStore } from '@/stores/sorting';
import { useToastStore } from '@/stores/toast';
import type { Customer, CustomerFilter, PaginatedResponse } from '@/types';
import { log } from '@/utils/log';
import { readPageSize, storePageSize } from '@/utils/pageSize';
import { defineStore } from 'pinia';
import { computed, type Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const storeId = 'customers';

/**
 * localStorage key for the persisted offline customer snapshot. Exported so tests
 * and external cleanup can wipe it deterministically.
 */
export const CUSTOMERS_PERSIST_KEY = 'app.customers';

/** Fallback cache key when no active company is resolved (defensive only). */
const GLOBAL_KEY = '__global__';

export const useCustomersStore = defineStore(
    storeId,
    () => {
        const authStore = useAuthStore();
        const toast = useToastStore();
        const { t } = useI18n();
        const models = ref<Customer[]>([]);
        const currentPage = ref(1);
        const totalPages = ref(1);
        const totalItems = ref(0);
        const itemsPerPage = ref(ITEMS_PER_PAGE);
        const isLoading = ref(false);
        let latestListRequest = 0;

        // Persisted, company-keyed snapshot of the most recent unfiltered first page of
        // customers. Used purely as an offline fallback so the customer picker is not
        // empty when a load fails offline. It deliberately survives the login/logout
        // `$reset` (not cleared there) so an offline cold boot still has data, but is
        // wiped on signOut because it is PII — see clearOfflineCache + auth.signOut.
        const customersByCompany = ref<Record<string, Customer[]>>({});

        const filter = ref<CustomerFilter>({
            name: null,
            type: null,
            group_id: null,
        });

        const sortStore = useSortingStore(storeId);

        // Computed property: only show customers from active company
        const filteredModels = computed(() => {
            if (!authStore.activeCompany) {
                return models.value;
            }
            return models.value.filter((customer) => customer.company_id === authStore.activeCompany?.id);
        });

        // Watch for active company changes and reload customers
        watch(
            () => authStore.activeCompany?.id,
            async (newCompanyId, oldCompanyId) => {
                if (newCompanyId !== oldCompanyId && newCompanyId) {
                    // Reset to first page when company changes
                    await loadPage(1);
                }
            },
        );

        const loader: PaginationLoader<Customer> = new PaginationLoader<Customer>(
            new (class implements PaginationLoaderDelegate<Customer> {
                getModels(): Ref<Customer[]> {
                    return filteredModels;
                }

                async loadPage(page: number): Promise<PaginatedResponse<Customer[]>> {
                    const activeCompanyId = authStore.activeCompany?.id;
                    const filterWithCompany = activeCompanyId
                        ? { ...filter.value, company_id: activeCompanyId }
                        : filter.value;
                    return await apiService.customer.index(page, filterWithCompany).then((response) => response.data);
                }
            })(),
        );

        function cacheKey(): string {
            return authStore.activeCompany?.id ?? GLOBAL_KEY;
        }

        async function loadPage(page: number) {
            const requestId = ++latestListRequest;
            const requestedFilter = { ...filter.value };
            isLoading.value = true;
            try {
                // Build filter with active company ID
                const activeCompanyId = authStore.activeCompany?.id;
                const filterWithCompany = activeCompanyId
                    ? { ...requestedFilter, company_id: activeCompanyId }
                    : requestedFilter;

                const response = await apiService.customer.index(
                    page,
                    filterWithCompany,
                    sortStore.currentSort,
                    readPageSize(storeId),
                );
                if (
                    requestId !== latestListRequest ||
                    JSON.stringify(requestedFilter) !== JSON.stringify(filter.value)
                ) {
                    return;
                }
                models.value = response.data.data;
                currentPage.value = response.data.meta.current_page;
                totalPages.value = response.data.meta.last_page;
                totalItems.value = response.data.meta.total;
                itemsPerPage.value = response.data.meta.per_page;

                // Snapshot the canonical (unfiltered) first page for offline fallback.
                // Filtered/search loads are skipped so they can't pollute the snapshot.
                if (page === 1 && !isFiltered()) {
                    customersByCompany.value = {
                        ...customersByCompany.value,
                        [cacheKey()]: response.data.data,
                    };
                }
            } catch (error) {
                if (
                    requestId !== latestListRequest ||
                    JSON.stringify(requestedFilter) !== JSON.stringify(filter.value)
                ) {
                    return;
                }
                log('Failed to load customers:', error);
                // Offline: fall back to the last cached snapshot so the customer picker
                // is not empty. Online errors keep the existing (empty) behaviour.
                if (!navigator.onLine) {
                    const cached = customersByCompany.value[cacheKey()];
                    if (cached?.length) {
                        log('Offline — serving cached customers');
                        models.value = cached;
                        currentPage.value = 1;
                        totalPages.value = 1;
                        totalItems.value = cached.length;
                    }
                }
            } finally {
                if (requestId === latestListRequest) {
                    isLoading.value = false;
                }
            }
        }

        async function resetFilter() {
            filter.value = {
                name: null,
                type: null,
                group_id: null,
            };
            await loadPage(1);
        }

        async function applyFilter() {
            await loadPage(1);
        }

        async function deleteCustomer(customerId: string) {
            try {
                await apiService.customer.delete(customerId);
                toast.success(t('customers.customerDeleted'));
            } catch (error) {
                log('Failed to delete customer:', error);
                toast.error(t('error.generic'));
                throw error;
            }
        }

        async function validateVATNumber(vatNumber: string) {
            try {
                const response = await apiService.customer.validateVATNumber(vatNumber);
                return response.data.data;
            } catch (error) {
                log('Failed to validate VAT number:', error);
                throw error;
            }
        }

        async function getCountries() {
            try {
                const response = await apiService.customer.populators();
                return response.data.data.countries;
            } catch (error) {
                log('Failed to fetch countries:', error);
                throw error;
            }
        }

        function clearModels() {
            models.value = [];
        }

        /**
         * Proactively cache the unfiltered first page of customers for the active
         * company while online, so the picker still works after going offline.
         * Fire-and-forget: never throws. Skips the request when a snapshot already
         * exists (organic page-1 loads keep it fresh) or when offline.
         */
        async function warmOfflineCache(): Promise<void> {
            if (!navigator.onLine) return;
            const key = cacheKey();
            if (customersByCompany.value[key]?.length) return;
            try {
                const activeCompanyId = authStore.activeCompany?.id;
                const response = await apiService.customer.index(
                    1,
                    activeCompanyId ? { company_id: activeCompanyId } : {},
                    sortStore.currentSort,
                    itemsPerPage.value,
                );
                customersByCompany.value = { ...customersByCompany.value, [key]: response.data.data };
            } catch (error) {
                log('Failed to warm customer cache:', error);
            }
        }

        /** Wipe the persisted offline snapshot. Called on signOut — it is PII. */
        function clearOfflineCache(): void {
            customersByCompany.value = {};
        }

        // The list lives in a closure-private `models` ref (only the filtered computed
        // is exposed), so the generic reset plugin in src/pinia.ts can't reach it.
        // This hand-written $reset is what `$ResetPinia().all()` calls to wipe a
        // previous user's customers on login/logout.
        function $reset() {
            models.value = [];
            currentPage.value = 1;
            totalPages.value = 1;
            totalItems.value = 0;
            itemsPerPage.value = ITEMS_PER_PAGE;
            isLoading.value = false;
            filter.value = { name: null, type: null, group_id: null };
        }

        function isFiltered(): boolean {
            const defaultFilter: CustomerFilter = {
                name: null,
                type: null,
                group_id: null,
            };
            return JSON.stringify(filter.value) !== JSON.stringify(defaultFilter);
        }

        /** Switches this list to another page size, remembers it and reloads from page 1. */
        async function setItemsPerPage(size: number) {
            if (!storePageSize(storeId, size)) return;
            itemsPerPage.value = size;
            await loadPage(1);
        }

        return {
            filter,
            sortStore,
            loader,
            models: filteredModels, // Export filtered models based on active company
            customersByCompany,
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage,
            setItemsPerPage,
            isLoading,
            getCountries,
            loadPage,
            applyFilter,
            resetFilter,
            deleteCustomer,
            isFiltered,
            validateVATNumber,
            clearModels,
            warmOfflineCache,
            clearOfflineCache,
            $reset,
        };
    },
    {
        // Only the offline snapshot is persisted. The hand-written $reset above does NOT
        // clear it, so it survives the login/logout reset for offline cold boots; it is
        // wiped explicitly on signOut (PII) via clearOfflineCache.
        persist: {
            key: CUSTOMERS_PERSIST_KEY,
            pick: ['customersByCompany'],
            storage: localStorage,
        } as any,
    },
);
